/** API uses "Twitter"; UI / mock label is "X". */
function normalizePlatformCategoryKey(name) {
  return name === 'Twitter' ? 'X' : name;
}

function categoryKeyForDimension(dimension, rawName) {
  if (dimension === 'platform') return normalizePlatformCategoryKey(rawName);
  return rawName;
}

function formatMonthLabel(yyyyMm) {
  const [yyyy, mm] = String(yyyyMm).split('-');
  const year = Number(yyyy);
  const monthIndex = Number(mm) - 1;
  if (!Number.isFinite(year) || !Number.isFinite(monthIndex) || monthIndex < 0 || monthIndex > 11) {
    return String(yyyyMm);
  }

  const monthShort = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'][monthIndex];
  return `${monthShort} ${String(yyyy).slice(-2)}`;
}

/**
 * Rolls the API response into month buckets suitable for a stacked bar chart.
 * Also returns category totals to drive the legend + donut.
 *
 * @param {Record<string, { language?: Record<string, number>, platform?: Record<string, number> }>|null|undefined} raw
 * @param {'language'|'platform'} dimension
 * @param {{
 *   topN?: number,
 *   preferredOrder?: string[],
 *   excludeFromRanking?: string[],
 *   groupRemainingAs?: string
 * }} options
 * @returns {{
 *   monthData: Array<{ month: string, [category: string]: number }>,
 *   categories: string[],
 *   categoryTotals: Record<string, number>,
 *   total: number
 * }}
 */
export function rollupLandingStatisticsByMonth(raw, dimension, options = {}) {
  const topN = options.topN ?? 5;
  const preferredOrder = Array.isArray(options.preferredOrder) ? options.preferredOrder : null;
  const excludeFromRanking = Array.isArray(options.excludeFromRanking)
    ? new Set(options.excludeFromRanking)
    : null;
  const groupRemainingAs =
    typeof options.groupRemainingAs === 'string' && options.groupRemainingAs
      ? options.groupRemainingAs
      : null;

  const safeRaw = raw && typeof raw === 'object' ? raw : null;
  if (!safeRaw) {
    return { monthData: [], categories: [], categoryTotals: {}, total: 0 };
  }

  const monthKeys = Object.keys(safeRaw).sort();

  const monthData = monthKeys.map((monthKey) => ({ month: formatMonthLabel(monthKey) }));
  const monthIndexByKey = new Map(monthKeys.map((k, idx) => [k, idx]));

  // Per-month, per-category raw counts (before any "Others" rollup).
  const monthCategoryCounts = monthKeys.map(() => ({}));
  const categoryTotals = {};

  for (let i = 0; i < monthKeys.length; i += 1) {
    const monthKey = monthKeys[i];
    const bucket = safeRaw[monthKey]?.[dimension];
    if (!bucket || typeof bucket !== 'object') continue;

    const monthIdx = monthIndexByKey.get(monthKey);
    if (monthIdx == null) continue;

    const entries = Object.entries(bucket);
    const counts = monthCategoryCounts[monthIdx];
    for (let j = 0; j < entries.length; j += 1) {
      const name = categoryKeyForDimension(dimension, entries[j][0]);
      const count = entries[j][1];
      const n = Number(count);
      if (!Number.isFinite(n)) continue;

      counts[name] = (counts[name] || 0) + n;
      categoryTotals[name] = (categoryTotals[name] || 0) + n;
    }
  }

  // Determine which categories are eligible to occupy a top-N slot.
  const rankingTotals = {};
  const allNames = Object.keys(categoryTotals);
  for (let i = 0; i < allNames.length; i += 1) {
    const name = allNames[i];
    if (excludeFromRanking && excludeFromRanking.has(name)) continue;
    rankingTotals[name] = categoryTotals[name];
  }

  // If we're grouping the remainder, reserve one slot for the "Others" bucket.
  const topSlots = groupRemainingAs ? Math.max(1, topN - 1) : topN;

  let topCategories;
  if (preferredOrder) {
    // Choose the top-N by totals, but preserve the visual/legend order from `preferredOrder`.
    const scored = preferredOrder
      .filter((name) => !excludeFromRanking || !excludeFromRanking.has(name))
      .map((name) => ({ name, total: rankingTotals[name] || 0 }));
    const topSet = new Set(
      scored
        .slice()
        .sort((a, b) => b.total - a.total)
        .slice(0, topSlots)
        .map((x) => x.name)
    );
    topCategories = preferredOrder.filter((name) => topSet.has(name));
  } else {
    topCategories = Object.entries(rankingTotals)
      .map(([name, total]) => ({ name, total }))
      .sort((a, b) => b.total - a.total)
      .slice(0, topSlots)
      .map((x) => x.name);
  }

  let categories = topCategories.slice();

  if (groupRemainingAs) {
    const topSet = new Set(topCategories);
    let othersTotal = 0;
    for (let i = 0; i < monthData.length; i += 1) {
      const counts = monthCategoryCounts[i];
      const names = Object.keys(counts);
      let monthOthers = 0;
      for (let j = 0; j < names.length; j += 1) {
        const name = names[j];
        if (topSet.has(name)) continue;
        monthOthers += counts[name];
      }
      if (monthOthers > 0) {
        monthData[i][groupRemainingAs] = (monthData[i][groupRemainingAs] || 0) + monthOthers;
        othersTotal += monthOthers;
      }
    }
    if (othersTotal > 0) {
      categoryTotals[groupRemainingAs] = (categoryTotals[groupRemainingAs] || 0) + othersTotal;
      categories.push(groupRemainingAs);
    }
  }

  // Copy per-month counts for the top categories into the chart rows.
  for (let i = 0; i < monthData.length; i += 1) {
    const counts = monthCategoryCounts[i];
    for (let j = 0; j < topCategories.length; j += 1) {
      const cat = topCategories[j];
      if (Number.isFinite(counts[cat])) {
        monthData[i][cat] = counts[cat];
      }
    }
  }

  // Normalize monthData so each category exists (stacking expects consistent data keys).
  for (let i = 0; i < monthData.length; i += 1) {
    for (let j = 0; j < categories.length; j += 1) {
      const cat = categories[j];
      const current = monthData[i][cat];
      monthData[i][cat] = Number.isFinite(current) ? current : 0;
    }
  }

  const total = categories.reduce((sum, name) => sum + (categoryTotals[name] || 0), 0);
  return { monthData, categories, categoryTotals, total };
}
